/**
 * Page of subscription selection in case of top-up subscription.
 *
 * Copyright © Ircam Amplify 2026
 */
import Button from '@/components/Button/Button'
import FullScreenLayout from '@/components/FullScreenLayout'
import { accountUtil } from '@/config/accountUtil'
import { logger, LogTag } from '@/config/logger'
import { EXTERNAL_URLS } from '@/constant/externalUrl'
import { BillingInformationState } from '@/pages/BillingInformationState'
import {
  Price,
  StripeProductName,
  SubscriptionData,
  TextUtil,
  TierType,
} from '@amplify3/duplo-back-end'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

export interface TopUpSelectTierProps {
  subscriptionData?: SubscriptionData
  userTier: string
}

export interface SelectionCardProps {
  title: string
  description: string
  price: string | undefined | null
  priceNote?: string
  contentsComponent: any
  buttonComponent: any
}

export const TopUpSelectTier = (props: TopUpSelectTierProps) => {
  const ulClassName = 'mt-4 text-md space-y-2 text-gray-600 list-disc pl-5'
  const location = useLocation()
  const contactInformation = location?.state?.contactInformation
  const [isEuroCurrency, setIsEuroCurrency] = useState(true)
  const [packCreditsPrice, setPackCreditsPrice] = useState<Price | null>(null)
  const currencySymbol = isEuroCurrency ? '€' : '$'
  const creditPackPrice = packCreditsPrice?.unit_amount
    ? TextUtil.formatPrice(packCreditsPrice.unit_amount / 100, currencySymbol)
    : '-'

  useEffect(() => {
    const loadPrice = async () => {
      const topUpPrice = await accountUtil.getPrice(
        StripeProductName.TopUp,
        props.subscriptionData
      )
      logger.debug(LogTag.Util, 'price: ', topUpPrice)
      setPackCreditsPrice(topUpPrice)
      setIsEuroCurrency(topUpPrice?.currency === 'eur')
    }

    loadPrice()
  }, [])

  const handleContactClick = () => {
    window.location.href = EXTERNAL_URLS.CONTACT
  }

  const ContactButton = () => {
    return <Button label="Contact us" onClick={handleContactClick} />
  }

  const BuyCreditButton = () => {
    return (
      <Button
        as={Link}
        to="/checkout"
        state={
          {
            tier: TierType.TopUpCreditAimd,
            subscriptionFrequency: undefined,
            numberOfCreditsForSubscription: 2000,
            priceIdList: packCreditsPrice?.id,
            contactInformation: contactInformation,
            planName: 'Top-Up',
            isEuroCurrency: isEuroCurrency,
            offPackageCreditCost: 0,
          } as BillingInformationState
        }
        label={packCreditsPrice ? 'Buy credits' : 'Loading...'}
        className="w-full bg-green-500 text-white py-2 rounded-md"
      />
    )
  }

  const SelectionCard = (props: SelectionCardProps) => {
    return (
      <div
        className="bg-white rounded-xl p-6
                min-w-96
            flex flex-col justify-between
            shadow-lg border border-gray-300"
      >
        <div>
          <h3
            className="
                text-xl font-semibold
                mb-2 text-black"
          >
            {props.title}
          </h3>
          <p className="text-md mb-4 text-gray-600">{props.description}</p>
          <p className="text-3xl font-bold text-black">{props.price}</p>
          <p className="text-sm text-black mt-1">{props.priceNote}</p>
        </div>
        <div>{props.contentsComponent}</div>
        <div className="mt-6">
          <div>{props.buttonComponent}</div>
        </div>
      </div>
    )
  }

  return (
    <FullScreenLayout
      className="!h-auto min-h-screen bg-white text-black"
      title="Simple, transparent pricing"
      subtitle={<p>Choose the plan that fits your usage</p>}
    >
      {/* Pricing cards in a grid */}
      <div
        className="mt-16 grid grid-cols-1 md:grid-cols-2
                   max-w-5xl mx-auto
                   justify-items-center"
      >
        {/* Pack credits */}
        <SelectionCard
          title="Top-up"
          description="One-time purchase, no renewal."
          price={creditPackPrice}
          contentsComponent={
            <ul className={ulClassName}>
              <li>2000 tracks</li>
              <li>No expiration</li>
              <li>No over-consumption</li>
              <li>No subscription</li>
            </ul>
          }
          buttonComponent={
            props.userTier === TierType.TopUpCreditAimd ? (
              <BuyCreditButton />
            ) : (
              <ContactButton />
            )
          }
        />

        {/* Enterprise plan remains unchanged */}
        <SelectionCard
          title="Enterprise"
          description="Tailored for organizations with large scale usage."
          price="On demand"
          priceNote="Custom plan"
          contentsComponent={
            <ul className={ulClassName}>
              <li>Unlimited tracks</li>
              <li>Dedicated account manager</li>
              <li>Custom integrations</li>
              <li>Premium support</li>
            </ul>
          }
          buttonComponent={<ContactButton />}
        />
      </div>
    </FullScreenLayout>
  )
}
